/**
 * WaitlistManagement.js
 * Admin interface for managing waitlist entries
 * 
 * FEATURES:
 * - View all waitlist entries with filtering
 * - Add users to waitlist manually
 * - Add & Approve in one step (creates account immediately)
 * - Approve pending entries (creates user account, shows temp password)
 * - Deny pending entries
 */

import React, { useState, useEffect, useRef } from 'react';
import { format } from 'date-fns';
import { Plus, X, Copy, Check } from 'lucide-react';
import { apiService } from '../../services/api';
import useFocusTrap from '../../hooks/useFocusTrap';

/**
 * Extract a user-facing error message from a failed axios request.
 *
 * The admin waitlist "already exists" 400 returns a payload shaped
 *   { message: "A user account already exists for this email (tier: <tier>, created MM/DD/YYYY).",
 *     existingUser: { tier, isActive, isVerified, createdAt } }
 *
 * We display the server-provided `message` directly — it already carries the
 * enriched tier/createdAt summary. The `existingUser` object is kept available
 * on the response so a future UI can render a secondary badge/detail block
 * without an extra round trip. Older responses that only carry `message` fall
 * through the same path.
 */
const extractAdminErrorMessage = (error, fallback = 'An error occurred. Please try again.') => {
  return error?.response?.data?.message || error?.message || fallback;
};

const WaitlistManagement = () => {
  // =========================================
  // State Management
  // =========================================
  
  const [waitlistEntries, setWaitlistEntries] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all'); // 'all', 'pending', 'approved', 'denied'
  const [showAddForm, setShowAddForm] = useState(false);
  
  // Add user form state
  const [formData, setFormData] = useState({
    email: '',
    firstName: '',
    lastName: '',
    notes: ''
  });
  
  // Form UI states
  const [isFormSubmitting, setIsFormSubmitting] = useState(false);
  const [formError, setFormError] = useState('');
  const [formSuccess, setFormSuccess] = useState(false);
  
  // Password modal state (shown after approval)
  const [passwordModal, setPasswordModal] = useState({
    show: false,
    email: '',
    firstName: '',
    lastName: '',
    temporaryPassword: '',
    welcomeEmailSent: false
  });
  const [passwordCopied, setPasswordCopied] = useState(false);
  const passwordModalRef = useRef(null);

  // =========================================
  // Focus Trap for Password Modal
  // =========================================
  useFocusTrap(passwordModalRef, passwordModal.show);

  // =========================================
  // Escape Key Handler for Password Modal
  // =========================================
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape' && passwordModal.show) {
        setPasswordModal({
          show: false,
          email: '',
          firstName: '',
          lastName: '',
          temporaryPassword: '',
          welcomeEmailSent: false
        });
        setPasswordCopied(false);
      }
    };

    if (passwordModal.show) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [passwordModal.show]);

  // =========================================
  // Data Fetching
  // =========================================

  const fetchWaitlist = async () => {
    setIsLoading(true);
    setError(null);
    try {
      console.log('Fetching waitlist entries');
      const entries = await apiService.admin.getWaitlist();
      console.log(`Fetched ${entries.length} waitlist entries`);
      setWaitlistEntries(entries);
    } catch (err) {
      console.error('Error fetching waitlist:', err);
      setError('Failed to load waitlist entries. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchWaitlist();
  }, []);

  // =========================================
  // Approval Handlers
  // =========================================

  /**
   * Handle approving a waitlist entry
   * This creates a user account and shows the temporary password
   */
  const handleApprove = async (id) => {
    try {
      console.log(`Approving waitlist entry: ${id}`);
      const response = await apiService.admin.approveWaitlistEntry(id);
      console.log(`Successfully approved waitlist entry: ${id}`);
      
      // Show password modal
      setPasswordModal({
        show: true,
        email: response.entry.email,
        firstName: response.entry.firstName,
        lastName: response.entry.lastName,
        temporaryPassword: response.temporaryPassword,
        welcomeEmailSent: response.welcomeEmailSent
      });

      // Update local state to show approved status
      setWaitlistEntries(prev => 
        prev.map(entry => 
          entry._id === id 
            ? { ...entry, status: 'approved', approvalDate: new Date() } 
            : entry
        )
      );
    } catch (err) {
      console.error('Error approving waitlist entry:', err);
      setError(err.response?.data?.message || 'Failed to approve waitlist entry. Please try again later.');
    }
  };

  /**
   * Handle denying a waitlist entry
   */
  const handleDeny = async (id) => {
    try {
      console.log(`Denying waitlist entry: ${id}`);
      await apiService.admin.denyWaitlistEntry(id);
      console.log(`Successfully denied waitlist entry: ${id}`);
      
      // Update local state to show denied status
      setWaitlistEntries(prev => 
        prev.map(entry => 
          entry._id === id 
            ? { ...entry, status: 'denied', approvalDate: new Date() } 
            : entry
        )
      );
    } catch (err) {
      console.error('Error denying waitlist entry:', err);
      setError('Failed to deny waitlist entry. Please try again later.');
    }
  };

  // =========================================
  // Add Form Handlers
  // =========================================

  const toggleAddForm = () => {
    setShowAddForm(!showAddForm);
    if (formError) setFormError('');
    if (formSuccess) setFormSuccess(false);
    // Reset form when closing
    if (showAddForm) {
      setFormData({
        email: '',
        firstName: '',
        lastName: '',
        notes: ''
      });
    }
  };
  
  const handleFormChange = (e) => {
    const { id, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [id]: value
    }));
  };
  
  /**
   * Add to waitlist only (status: pending)
   */
  const handleAddToWaitlist = async (e) => {
    e.preventDefault();
    setIsFormSubmitting(true);
    setFormError('');
    setFormSuccess(false);

    try {
      console.log('Adding user to waitlist:', {
        email: formData.email,
        firstName: formData.firstName,
        lastName: formData.lastName
      });
      
      await apiService.admin.addToWaitlist(formData);
      console.log('Successfully added user to waitlist');
      
      setFormSuccess(true);
      setFormData({
        email: '',
        firstName: '',
        lastName: '',
        notes: ''
      });
      
      // Refresh waitlist entries
      fetchWaitlist();
    } catch (error) {
      console.error('Waitlist addition error:', error);
      setFormError(extractAdminErrorMessage(error));
    } finally {
      setIsFormSubmitting(false);
    }
  };

  /**
   * Add to waitlist AND approve immediately (creates account)
   */
  const handleAddAndApprove = async (e) => {
    e.preventDefault();
    setIsFormSubmitting(true);
    setFormError('');
    setFormSuccess(false);

    try {
      console.log('Add and approve user:', {
        email: formData.email,
        firstName: formData.firstName,
        lastName: formData.lastName
      });
      
      const response = await apiService.admin.addAndApproveWaitlist(formData);
      console.log('Successfully added and approved user');
      
      // Show password modal
      setPasswordModal({
        show: true,
        email: response.entry.email,
        firstName: response.entry.firstName,
        lastName: response.entry.lastName,
        temporaryPassword: response.temporaryPassword,
        welcomeEmailSent: response.welcomeEmailSent
      });

      setFormData({
        email: '',
        firstName: '',
        lastName: '',
        notes: ''
      });
      
      // Refresh waitlist entries
      fetchWaitlist();
      
      // Close the add form
      setShowAddForm(false);
    } catch (error) {
      console.error('Add and approve error:', error);
      setFormError(extractAdminErrorMessage(error));
    } finally {
      setIsFormSubmitting(false);
    }
  };

  // =========================================
  // Password Modal Handlers
  // =========================================

  const closePasswordModal = () => {
    setPasswordModal({
      show: false,
      email: '',
      firstName: '',
      lastName: '',
      temporaryPassword: '',
      welcomeEmailSent: false
    });
    setPasswordCopied(false);
  };

  const copyPasswordToClipboard = () => {
    navigator.clipboard.writeText(passwordModal.temporaryPassword);
    setPasswordCopied(true);
    setTimeout(() => setPasswordCopied(false), 2000);
  };

  // =========================================
  // Filtering
  // =========================================

  const filteredEntries = waitlistEntries.filter(entry => {
    if (filter === 'all') return true;
    return entry.status === filter;
  });

  // =========================================
  // Render: Loading State
  // =========================================

  if (isLoading && waitlistEntries.length === 0) {
    return <div className="loading-message">Loading waitlist entries...</div>;
  }

  // =========================================
  // Render: Main Component
  // =========================================

  return (
    <div className="waitlist-management">
      {/* Header Section */}
      <div className="waitlist-header">
        <div className="header-main">
          <h2>Waitlist Management</h2>
          <button 
            className={`add-user-toggle ${showAddForm ? 'active' : ''}`}
            onClick={toggleAddForm}
            title={showAddForm ? "Close form" : "Add new user to waitlist"}
          >
            {showAddForm ? <X size={20} /> : <Plus size={20} />}
            {showAddForm ? 'Close' : 'Add User'}
          </button>
        </div>
        
        {/* Add User Form */}
        {showAddForm && (
          <div className="add-waitlist-entry">
            <h3>Add New User</h3>
            
            {formError && <div className="error-message">{formError}</div>}
            {formSuccess && <div className="success-message">User added to waitlist successfully</div>}
            
            <form onSubmit={handleAddToWaitlist}>
              <div className="form-row">
                <div className="form-field">
                  <label htmlFor="email">Email *</label>
                  <input
                    type="email"
                    id="email"
                    value={formData.email}
                    onChange={handleFormChange}
                    required
                    disabled={isFormSubmitting}
                  />
                </div>
              </div>
              
              <div className="form-row">
                <div className="form-field">
                  <label htmlFor="firstName">First Name *</label>
                  <input
                    type="text"
                    id="firstName"
                    value={formData.firstName}
                    onChange={handleFormChange}
                    required
                    disabled={isFormSubmitting}
                  />
                </div>
                
                <div className="form-field">
                  <label htmlFor="lastName">Last Name *</label>
                  <input
                    type="text"
                    id="lastName"
                    value={formData.lastName}
                    onChange={handleFormChange}
                    required
                    disabled={isFormSubmitting}
                  />
                </div>
              </div>
              
              <div className="form-field">
                <label htmlFor="notes">Notes (optional, admin only)</label>
                <textarea
                  id="notes"
                  value={formData.notes}
                  onChange={handleFormChange}
                  rows={2}
                  disabled={isFormSubmitting}
                  placeholder="Internal notes about this user"
                />
              </div>
              
              <div className="form-actions">
                <button 
                  type="submit" 
                  className="add-button"
                  disabled={isFormSubmitting}
                >
                  {isFormSubmitting ? 'Adding...' : 'Add to Waitlist'}
                </button>
                <button 
                  type="button"
                  className="add-approve-button"
                  disabled={isFormSubmitting}
                  onClick={handleAddAndApprove}
                >
                  {isFormSubmitting ? 'Processing...' : 'Add & Approve'}
                </button>
              </div>
            </form>
          </div>
        )}
        
        {/* Filter Buttons */}
        <div className="filter-buttons">
          <button 
            className={filter === 'all' ? 'active' : ''}
            onClick={() => setFilter('all')}
          >
            All ({waitlistEntries.length})
          </button>
          <button 
            className={filter === 'pending' ? 'active' : ''}
            onClick={() => setFilter('pending')}
          >
            Pending ({waitlistEntries.filter(e => e.status === 'pending').length})
          </button>
          <button 
            className={filter === 'approved' ? 'active' : ''}
            onClick={() => setFilter('approved')}
          >
            Approved ({waitlistEntries.filter(e => e.status === 'approved').length})
          </button>
          <button 
            className={filter === 'denied' ? 'active' : ''}
            onClick={() => setFilter('denied')}
          >
            Denied ({waitlistEntries.filter(e => e.status === 'denied').length})
          </button>
        </div>
      </div>

      {/* Error Display */}
      {error && <div className="error-message">{error}</div>}

      {/* Waitlist Table */}
      {filteredEntries.length === 0 ? (
        <p className="no-entries">No waitlist entries found.</p>
      ) : (
        <table className="waitlist-table">
          <thead>
            <tr>
              <th>Date</th>
              <th>Name</th>
              <th>Email</th>
              <th>Notes</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredEntries.map(entry => (
              <tr key={entry._id} className={entry.status}>
                <td>{format(new Date(entry.createdAt), 'MM/dd/yyyy')}</td>
                <td>{`${entry.firstName} ${entry.lastName}`}</td>
                <td>{entry.email}</td>
                <td className="notes-cell">{entry.notes || '-'}</td>
                <td>
                  <span className={`status-badge ${entry.status}`}>
                    {entry.status.charAt(0).toUpperCase() + entry.status.slice(1)}
                  </span>
                </td>
                <td>
                  {entry.status === 'pending' ? (
                    <div className="action-buttons">
                      <button 
                        className="approve-button"
                        onClick={() => handleApprove(entry._id)}
                      >
                        Approve
                      </button>
                      <button 
                        className="deny-button"
                        onClick={() => handleDeny(entry._id)}
                      >
                        Deny
                      </button>
                    </div>
                  ) : (
                    <span className="action-date">
                      {entry.approvalDate 
                        ? `${entry.status === 'approved' ? 'Approved' : 'Denied'} ${format(new Date(entry.approvalDate), 'MM/dd/yyyy')}`
                        : '-'}
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Password Modal */}
      {passwordModal.show && (
        <div className="modal-overlay" onClick={closePasswordModal}>
          <div
            className="modal-content password-modal"
            onClick={e => e.stopPropagation()}
            ref={passwordModalRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby="password-modal-title"
          >
            <h3 id="password-modal-title">Account Created Successfully</h3>
            <p>
              A new account has been created for <strong>{passwordModal.firstName} {passwordModal.lastName}</strong>.
            </p>
            
            <div className="credentials-box">
              <div className="credential-row">
                <label>Email:</label>
                <span>{passwordModal.email}</span>
              </div>
              <div className="credential-row">
                <label>Temporary Password:</label>
                <div className="password-display">
                  <code>{passwordModal.temporaryPassword}</code>
                  <button 
                    className="copy-button"
                    onClick={copyPasswordToClipboard}
                    title="Copy password"
                  >
                    {passwordCopied ? <Check size={16} /> : <Copy size={16} />}
                  </button>
                </div>
              </div>
            </div>
            
            {passwordModal.welcomeEmailSent ? (
              <p className="email-status email-status--success">
                A welcome email with a magic link has been sent to {passwordModal.email}.
                The temporary password above is a backup in case the email doesn't arrive.
              </p>
            ) : (
              <p className="email-status email-status--warning">
                Welcome email could not be sent. Please share the credentials below with the user securely.
                They should change their password after first login.
              </p>
            )}
            
            <button className="close-modal-button" onClick={closePasswordModal}>
              Done
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default WaitlistManagement;
